import { motion } from "framer-motion";
import { HomeHero } from "@/components/HomeHero";
import { Reveal, MaskLines, ClipImage } from "@/components/Motion";
import { TextLink, ConsultButton } from "@/components/Buttons";
import { Marquee } from "@/components/Marquee";
import { PlanOptions } from "@/components/PlanOptions";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { CTABand } from "@/components/CTABand";
import { SectionHead, usePageMeta } from "@/components/Common";
import { FullLogo } from "@/components/Logo";
import { SERVICES, AREAS, CONSULT_NOTE } from "@/data/site";
import { Testimonials } from "@/components/Testimonials";

const WhyFirstDue = () => (
  <section data-testid="why-first-due" className="relative bg-[#F8F7F3] py-24 md:py-36">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal className="mb-12 max-w-[300px] sm:max-w-[380px]"><FullLogo /></Reveal>
        <p className="eyebrow">Why First Due?</p>
        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-[#14263D] sm:text-5xl lg:text-6xl">
          <MaskLines onView lines={["A Lifetime of Service.", "A New Commitment", "to Your Home."]} />
        </h2>
        <Reveal delay={0.2} className="mt-10 border-l-2 border-[#A52A32] pl-6">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#5F6B76]">first due · <span className="normal-case">noun, fire service</span></p>
          <p className="mt-3 font-display text-2xl italic leading-snug text-[#14263D]">
            The company assigned first responsibility for responding to incidents within a particular area.
          </p>
        </Reveal>
      </div>
      <div className="space-y-6 text-base leading-relaxed text-[#5F6B76] md:text-lg lg:col-span-6 lg:col-start-7 lg:pt-16">
        <Reveal><p>In the fire service, the term <strong className="text-[#14263D]">First Due</strong> refers to the company assigned first responsibility for responding to incidents within a particular area.</p></Reveal>
        <Reveal delay={0.05}><p>For founder Dwight Saxon, those words represent preparation, responsibility, accountability and a lifelong commitment to serving others.</p></Reveal>
        <Reveal delay={0.1}><p>After 30 years with Palm Beach County Fire Rescue, Dwight brings those same values to First Due Home Watch &amp; Concierge.</p></Reveal>
        <Reveal delay={0.15}><p className="font-display text-3xl leading-snug text-[#14263D]">We believe homeowners deserve more than someone simply driving by their property.</p></Reveal>
        <Reveal delay={0.2}><p>They deserve scheduled visits, meaningful observations, professional reporting and a local contact who understands the importance of dependable service.</p></Reveal>
        <Reveal delay={0.25} className="pt-4"><TextLink to="/about" testId="meet-founder-btn">Meet Our Founder</TextLink></Reveal>
      </div>
    </div>
  </section>
);

const BENTO = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

const ServicesBento = () => (
  <section data-testid="services-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHead eyebrow="What We Do" title={<MaskLines onView lines={["Our Home Watch &", "Concierge Services"]} />} />
        <Reveal><TextLink to="/home-watch-services" testId="view-all-services-btn">View All Services</TextLink></Reveal>
      </div>
      <div className="mt-14 grid gap-6 lg:grid-cols-12">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.key}
            data-testid={`service-card-${s.key}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative flex min-h-[440px] flex-col justify-end overflow-hidden rounded-[4px] bg-[#14263D] ${BENTO[i]}`}
          >
            <img src={s.img} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0e1b2c] via-[#14263D]/70 to-[#14263D]/10" />
            <div className="relative p-8 md:p-10">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#f0b3b7]">0{i + 1} / 04</span>
              <h3 className="mt-3 max-w-md font-display text-3xl font-semibold leading-tight text-white md:text-4xl">{s.title}</h3>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/85 md:text-base">{s.text}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

const PlansPreview = () => (
  <section data-testid="plans-preview" className="rule-dots bg-[#F8F7F3] py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <SectionHead eyebrow="Home Watch Plans" title={<MaskLines onView lines={["Professional Home Watch & Concierge", "Services Tailored to Your Property."]} />}>
        <p>Every plan is built around your home and how often you need us. Each visit includes documented observations, photographs and a digital report, and every client receives a clear written quote before services begin.</p>
      </SectionHead>
      <div className="mt-14"><PlanOptions /></div>
      <div className="mt-14 flex flex-col justify-between gap-6 border-t border-[#14263D]/15 pt-8 md:flex-row md:items-center">
        <p data-testid="pricing-note" className="max-w-2xl text-sm text-[#5F6B76]">{CONSULT_NOTE}</p>
        <div className="flex flex-wrap items-center gap-6">
          <ConsultButton testId="home-quote-btn" label="Request Your Personalized Home Watch Quote" />
          <TextLink to="/plans-and-pricing" testId="compare-plans-btn">View Home Watch Plans</TextLink>
        </div>
      </div>
    </div>
  </section>
);

const Community = () => (
  <section data-testid="community-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="Proudly Serving Our Local Communities" title={<MaskLines onView lines={["Your Neighbors.", "Your Community.", "Your Home."]} />}>
          <p>First Due Home Watch &amp; Concierge is focused on providing personal service throughout western Palm Beach County.</p>
        </SectionHead>
        <Reveal className="mt-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#5F6B76]">Our initial service areas include</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {AREAS.map((a) => <li key={a} className="rounded-full border border-[#14263D]/20 px-4 py-2 text-sm font-medium text-[#14263D]">{a}</li>)}
          </ul>
          <p className="mt-6 text-base leading-relaxed text-[#5F6B76]">By keeping our service area local, we can develop strong customer relationships and maintain efficient property visit schedules.</p>
          <div className="mt-8"><TextLink to="/service-areas" testId="explore-areas-btn">Explore Our Service Areas</TextLink></div>
        </Reveal>
      </div>
      <div className="grid gap-6 lg:col-span-7 lg:grid-cols-5">
        <ClipImage src="/images/street.jpg" alt="Palm-lined residential street in western Palm Beach County" className="h-64 rounded-[4px] lg:col-span-2 lg:h-full" />
        <Reveal className="lg:col-span-3"><ServiceAreaMap /></Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(
    "First Due Home Watch & Concierge | Wellington & Western Palm Beach County",
    "Professional home watch & concierge in Wellington, Royal Palm Beach, Westlake, Arden, Loxahatchee & Loxahatchee Groves. Veteran-owned, firefighter-founded. Request a personalized quote."
  );
  return (
    <main data-testid="home-page">
      <HomeHero />
      <section className="bg-[#14263D] py-20 text-white md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <p className="max-w-5xl font-display text-3xl leading-[1.2] md:text-5xl">
              Whether you're spending the summer up north, traveling for several weeks or leaving your Florida residence unoccupied, we provide <em className="text-[#f0b3b7]">scheduled visual home checks</em>, <em className="text-[#f0b3b7]">detailed photographic reports</em> and personalized local assistance.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
              Our goal is simple: help you stay informed about your property's condition and provide a dependable local contact while you're away.
            </p>
          </Reveal>
        </div>
      </section>
      <WhyFirstDue />
      <ServicesBento />
      <Marquee dark />
      <PlansPreview />
      <Testimonials />
      <Community />
      <CTABand testId="home-final-cta" />
    </main>
  );
}
